import './HvacSettingsView.scss';
import { EnergySource, HvacArea } from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { memo, useCallback } from 'react';
import { FormError } from 'src/modules/form/types/FormError';
import { HvacFormData } from 'src/modules/hvac/types/HvacFormData';
import { HvacSettingsCommentCardView } from 'src/modules/hvac/views/HvacSettingsCommentCardView';
import { HvacSettingsCoolingCardView } from 'src/modules/hvac/views/HvacSettingsCoolingCardView';
import { HvacSettingsEnergyPricesCardView } from 'src/modules/hvac/views/HvacSettingsEnergyPricesCardView';
import { HvacSettingsHeatingCardView } from 'src/modules/hvac/views/HvacSettingsHeatingCardView';
import { HvacSettingsVentilationCardView } from 'src/modules/hvac/views/HvacSettingsVentilationCardView';

type Props = {
  readonly form: FormController<HvacFormData, FormError>;
  readonly readOnly: boolean;
};

export const HvacSettingsView = memo(({ form, readOnly }: Props) => {
  const commentField = form.field.at('assessment').at('comment');
  const areasField = form.field.at('assessment').at('areas');
  const heatingEnabled = useFormWatch(form, areasField.at(HvacArea.HEATING).at('enabled').value.get);
  const coolingEnabled = useFormWatch(form, areasField.at(HvacArea.COOLING).at('enabled').value.get);

  const resetEnabled = useCallback((area: HvacArea) => {
    areasField.form.change(pipe(
      areasField.form.currentState,
      areasField.at(area).at('enabled').value.set(false),
    ));
  }, [areasField]);

  const resetThermal = useCallback((area: HvacArea) => {
    areasField.form.change(pipe(
      areasField.form.currentState,
      areasField.at(area).at('thermal').value.set({
        energySource: EnergySource.ELECTRICITY,
        annualEnergyUse: null,
        carbonIntensity: null,
      }),
    ));
  }, [areasField]);

  const resetElectrical = useCallback((area: HvacArea) => {
    areasField.form.change(pipe(
      areasField.form.currentState,
      areasField.at(area).at('electrical').value.set({
        energySource: EnergySource.ELECTRICITY,
        annualEnergyUse: null,
        carbonIntensity: null,
      }),
    ));
  }, [areasField]);

  const resetSection = useCallback((area: HvacArea) => {
    resetEnabled(area);

    if (area !== HvacArea.VENTILATION) {
      resetThermal(area);
    }

    resetElectrical(area);
  }, [resetEnabled, resetThermal, resetElectrical]);

  return (
    <div className="bp-hvac-settings-view">
      <HvacSettingsHeatingCardView
        path={areasField.at(HvacArea.HEATING)}
        resetSection={resetSection}
        readOnly={readOnly}
      />

      <HvacSettingsCoolingCardView
        path={areasField.at(HvacArea.COOLING)}
        resetSection={resetSection}
        readOnly={readOnly}
      />

      <HvacSettingsVentilationCardView
        resetSection={resetSection}
        path={areasField.at(HvacArea.VENTILATION)}
        readOnly={readOnly}
      />

      <HvacSettingsEnergyPricesCardView
        form={form}
        coolingEnabled={coolingEnabled}
        heatingEnabled={heatingEnabled}
        readOnly={readOnly}
      />

      <HvacSettingsCommentCardView
        path={commentField}
        readOnly={readOnly}
      />
    </div>
  );
});
